import React from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';

// Aurora background moves slowly to give a premium feel
const aurora = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Shimmer sweep passes over content blocks using background-position for a smooth full-length travel
const sweep = keyframes`
  0% { transform: translateX(-150%); }
  100% { transform: translateX(450%); }
`;

export interface FancySkeletonOverrides {
  shimmerSpeedSec?: number;         // shimmer duration
  shimmerDirection?: 'ltr' | 'rtl'; // default ltr
  bandWidthPct?: number;            // width of bright band around 50% (e.g., 10)
  bandOpacity?: number;             // scales band brightness 0..1
  auroraSpeedSec?: number;          // background aurora duration
}

interface FancySkeletonProps extends FancySkeletonOverrides {
  height: number | string;
  width?: number | string;
  radius?: number;
}

export const FancySkeleton: React.FC<FancySkeletonProps> = ({
  height,
  width = '100%',
  radius = 12,
  shimmerSpeedSec = 2.0,
  shimmerDirection = 'ltr',
  bandWidthPct = 30,
  bandOpacity = 0.9,
  auroraSpeedSec = 4
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const base = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const glow1 = isDark ? 'rgba(0, 210, 255, 0.16)' : 'rgba(0, 122, 158, 0.10)';
  const glow2 = isDark ? 'rgba(0, 122, 255, 0.12)' : 'rgba(0, 51, 78, 0.08)';
  const glow3 = isDark ? 'rgba(120, 0, 245, 0.16)' : 'rgba(0, 122, 158, 0.10)';

  // Compute gradient params
  const bw = Math.max(2, Math.min(40, bandWidthPct));
  const leftStop = 50 - bw / 2;
  const rightStop = 50 + bw / 2;
  const midOp = (isDark ? 0.22 : 0.72) * Math.max(0, Math.min(1, bandOpacity));
  const sideOp = (isDark ? 0.14 : 0.55) * Math.max(0, Math.min(1, bandOpacity));
  
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width,
        height,
        borderRadius: radius,
        // Aurora gradient background
        background: `linear-gradient(120deg, ${glow1}, ${base} 30%, ${glow2} 60%, ${glow3})`,
        backgroundSize: '200% 200%',
        animation: `${aurora} ${auroraSpeedSec}s ease-in-out infinite`,
        // Subtle inner border
        boxShadow: isDark
          ? 'inset 0 0 0 1px rgba(255,255,255,0.06)'
          : 'inset 0 0 0 1px rgba(0,0,0,0.06)'
        ,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none'
        }
      }}
      role="status"
      aria-busy="true"
    >
      {/* Shimmer sweep overlay (transform-based for seamless loop) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: `${Math.max(12, Math.min(60, bandWidthPct * 2.6))}%`,
          background: `linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,${sideOp}) 30%, rgba(255,255,255,${midOp}) 50%, rgba(255,255,255,${sideOp}) 70%, rgba(255,255,255,0) 100%)`,
          filter: 'blur(6px)',
          animation: `${sweep} ${shimmerSpeedSec}s linear infinite`,
          animationDirection: shimmerDirection === 'rtl' ? 'reverse' as const : 'normal' as const,
          pointerEvents: 'none',
          '@media (prefers-reduced-motion: reduce)': {
            display: 'none'
          }
        }}
      />
    </Box>
  );
};

// A composite skeleton for reactions list (icon + two lines)
export const ReactionSkeletonList: React.FC<{ count?: number } & FancySkeletonOverrides> = ({ count = 3, ...rest }) => {
  return (
    <Stack spacing={1.5}>
      {Array.from({ length: count }).map((_, idx) => (
        <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
          <FancySkeleton height={36} width={36} radius={18} {...rest} />
          <Stack spacing={0.75} sx={{ flex: 1 }}>
            <FancySkeleton height={14} width="70%" radius={8} {...rest} />
            <FancySkeleton height={12} width="50%" radius={8} {...rest} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

// A composite skeleton for a chart/metrics card
export const MetricsSkeleton: React.FC<FancySkeletonOverrides> = (props) => (
  <Stack spacing={2}>
    <FancySkeleton height={28} width={200} {...props} />
    <FancySkeleton height={220} {...props} />
  </Stack>
);
